import React, { memo, useState, useEffect } from 'react';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Order } from '../../types/types';
import { XCircle, Truck } from 'lucide-react';
import { toast } from 'react-toastify';

// واجهة العناصر المستلمة (بدون رفض)
interface ItemReceipt {
  itemId: string;
  receivedQuantity: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  t: (key: string) => string;
  isRtl: boolean;
  confirmDelivery: (orderId: string, items: ItemReceipt[]) => Promise<void>;
  submitting: string | null;
}

// حقل الكمية – صغير، أنيق، ويدعم الحالة المعطلة
const QuantityInput: React.FC<{
  value: number;
  onChange: (val: number) => void;
  unit: string;
  t: (key: string) => string;
  disabled?: boolean;
}> = memo(({ value, onChange, unit, t, disabled = false }) => {
  const isKg = /كيلو|كجم|kg|kilo/i.test(unit);
  const step = isKg ? 0.25 : 1;
  const [display, setDisplay] = useState(value.toString());

  useEffect(() => {
    setDisplay(value.toString().replace(/\.0+$/, ''));
  }, [value]);

  const validate = (input: string) => {
    if (!input.trim()) return onChange(0);
    const num = parseFloat(input.replace(',', '.'));
    if (isNaN(num) || num < 0) return;

    if (isKg && Math.round(num * 100) % 25 !== 0) {
      toast.error(t('quantity_kg_step'));
      return;
    }
    if (!isKg && !Number.isInteger(num)) {
      toast.error(t('quantity_integer_only'));
      return;
    }
    onChange(num);
  };

  return (
    <div
      className={`flex items-center rounded-md text-xs overflow-hidden ${
        disabled
          ? 'opacity-70 cursor-not-allowed border border-gray-300'
          : 'border border-gray-300'
      } bg-white`}
    >
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - step))}
        disabled={disabled}
        className={`w-8 h-8 text-sm font-bold transition-colors ${
          disabled
            ? 'opacity-50 cursor-not-allowed text-gray-400'
            : 'hover:bg-gray-100 text-gray-700'
        }`}
        aria-label={t('decrease')}
      >
        −
      </button>

      <input
        type="text"
        value={display}
        onChange={(e) => !disabled && setDisplay(e.target.value.replace(/[^\d.,]/g, ''))}
        onBlur={(e) => !disabled && validate(e.target.value)}
        disabled={disabled}
        className="w-14 text-center font-semibold text-sm outline-none bg-transparent"
        style={{ direction: 'ltr' }}
      />

      <button
        type="button"
        onClick={() => onChange(value + step)}
        disabled={disabled}
        className={`w-8 h-8 text-sm font-bold transition-colors ${
          disabled
            ? 'opacity-50 cursor-not-allowed text-gray-400'
            : 'bg-amber-600 hover:bg-amber-700 text-white'
        }`}
        aria-label={t('increase')}
      >
        +
      </button>

      <span className="px-2 py-0.5 text-xs font-medium text-gray-600 bg-gray-50 border-l border-gray-300">
        {unit}
      </span>
    </div>
  );
});

const ConfirmDeliveryModal: React.FC<Props> = memo(
  ({ isOpen, onClose, order, t, isRtl, confirmDelivery, submitting }) => {
    const [items, setItems] = useState<ItemReceipt[]>([]);

    useEffect(() => {
      if (isOpen && order) {
        setItems(
          order.items.map((item) => ({
            itemId: item.itemId,
            receivedQuantity: item.rejected ? 0 : item.quantity,
          }))
        );
      } else {
        setItems([]);
      }
    }, [order, isOpen]);

    const handleQty = (id: string, val: number) => {
      setItems((prev) =>
        prev.map((i) => (i.itemId === id ? { ...i, receivedQuantity: val } : i))
      );
    };

    const submit = async () => {
      const hasZeroOrNegative = items.some((i) => i.receivedQuantity < 0);
      if (hasZeroOrNegative) {
        toast.error(t('quantity_must_be_0_or_positive'));
        return;
      }

      await confirmDelivery(order.id, items);
      onClose();
    };

    if (!isOpen || !order || items.length === 0) return null;

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t('confirm_delivery_title', { number: order.orderNumber })}
        className="max-w-2xl"
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        {/* قائمة العناصر: كل عنصر في سطر واحد */}
        <div className="max-h-[68vh] overflow-y-auto space-y-3 px-2">
          {order.items.map((item) => {
            const rec = items.find((r) => r.itemId === item.itemId)!;
            const isProductionRejected = item.rejected === true;
            const disabled = isProductionRejected;

            // تحديد الخلفية والحدود حسب الحالة
            let bgColor = 'bg-white';
            let borderColor = 'border-gray-200';
            if (isProductionRejected) {
              bgColor = 'bg-red-50';
              borderColor = 'border-red-300';
            } else if (rec.receivedQuantity > item.quantity) {
              bgColor = 'bg-green-50';
              borderColor = 'border-green-300';
            } else if (rec.receivedQuantity < item.quantity) {
              bgColor = 'bg-amber-50';
              borderColor = 'border-amber-300';
            }

            return (
              <div
                key={item.itemId}
                className={`rounded-lg border ${borderColor} ${bgColor} p-3 flex flex-row items-center justify-between`}
              >
                {/* معلومات المنتج */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-800 truncate">
                      {item.productName}
                    </span>
                    {isProductionRejected && (
                      <XCircle className="flex-shrink-0 w-4 h-4 text-red-600" />
                    )}
                  </div>
                  <span className="text-xs text-gray-600 mt-0.5">
                    {isRtl ? `المطلوب` : `Required}`} : {item.quantity} {item.unit}
                  </span>
                </div>

                {/* حقل الكمية */}
                <div className="flex-shrink-0">
                  <QuantityInput
                    value={rec.receivedQuantity}
                    onChange={(v) => handleQty(item.itemId, v)}
                    unit={item.unit}
                    t={t}
                    disabled={disabled}
                  />
                </div>
              </div>
            );
          })}
        </div>

        {/* أزرار الإجراءات - أصغر، أنظف، ومحاذاة ذكية حسب اللغة */}
        <div
          className={`flex gap-2 pt-4 border-t border-gray-200 mt-4 ${
            isRtl ? 'justify-end' : 'justify-end'
          }`}
        >
          <Button
            variant="secondary"
            onClick={onClose}
            className="text-xs px-4 py-1.5 rounded-md font-medium"
          >
            {t('cancel')}
          </Button>
          <Button
            variant="success"
            onClick={submit}
            loading={submitting === order.id}
            className="text-xs px-4 py-1.5 rounded-md font-medium flex items-center gap-1"
          >
{isRtl ? 'تأكيد' : 'Confirm'} 
            <Truck className="w-3.5 h-3.5" />
         </Button>
        </div>
      </Modal>
    );
  }
);

ConfirmDeliveryModal.displayName = 'ConfirmDeliveryModal';
export default ConfirmDeliveryModal;